<template>
  <validation-observer ref="formObserver">
    <b-form @submit.prevent="onSave" class="mt-1">
      <b-row>
        <b-col cols="12">
          <!-- Entré Settings Section -->
          <b-card class="mb-1 border">
            <h6 class="mb-2 font-weight-bold">{{ $t('dmp.remoteOptions.entreSettings') }}</h6>

            <b-row>
              <b-col cols="12" md="6">
                <b-form-group :label="$t('dmp.remoteOptions.entreConnection')" label-for="entreConnection">
                  <b-form-select
                    id="entreConnection"
                    v-model="remoteOptions.entreConnection"
                    :options="entreConnectionOptions"
                  />
                </b-form-group>

                <validation-provider
                  #default="validationContext"
                  :name="$t('dmp.remoteOptions.entreIncomingTcpPort')"
                  :rules="{
                    required: entreConnectionFieldsEnabled,
                    min_value: 0,
                    max_value: 65535,
                  }"
                >
                  <b-form-group
                    :label="$t('dmp.remoteOptions.entreIncomingTcpPort')"
                    label-for="entreInTcpPort"
                  >
                    <b-form-input
                      id="entreInTcpPort"
                      v-model.number="remoteOptions.entreInTcpPort"
                      type="number"
                      min="0"
                      max="65535"
                      placeholder="2011"
                      :disabled="!entreConnectionFieldsEnabled"
                      :state="getValidationState(validationContext)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <b-form-group :label="$t('dmp.remoteOptions.entreIp')" label-for="entreIpAddress">
                  <ip-address-input
                    :value="remoteOptions.entreIpAddress"
                    @input="val => remoteOptions.entreIpAddress = val"
                    :disabled="!entreConnectionFieldsEnabled"
                  />
                </b-form-group>

                <validation-provider
                  #default="validationContext"
                  :name="$t('dmp.remoteOptions.entreOutboundTcpPort')"
                  :rules="{ required: true, min_value: 0, max_value: 65535 }"
                >
                  <b-form-group
                    :label="$t('dmp.remoteOptions.entreOutboundTcpPort')"
                    label-for="entreOutboundTcpPort"
                  >
                    <b-form-input
                      id="entreOutboundTcpPort"
                      v-model.number="remoteOptions.entreOutboundTcpPort"
                      type="number"
                      min="0"
                      max="65535"
                      placeholder="2001"
                      :state="getValidationState(validationContext)"
                      :disabled="!entreConnectionFieldsEnabled"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <b-form-group :label="$t('dmp.remoteOptions.entreBackupIp')" label-for="entreBackupIpAddress">
                  <ip-address-input
                    :value="remoteOptions.entreBackupIpAddress"
                    @input="val => remoteOptions.entreBackupIpAddress = val"
                    :disabled="!entreConnectionFieldsEnabled"
                  />
                </b-form-group>

                <validation-provider
                  #default="validationContext"
                  :name="$t('dmp.remoteOptions.entreBackupTcpPort')"
                  :rules="{ required: true, min_value: 0, max_value: 65535 }"
                >
                  <b-form-group
                    :label="$t('dmp.remoteOptions.entreBackupTcpPort')"
                    label-for="entreBackupTcpPort"
                  >
                    <b-form-input
                      id="entreBackupTcpPort"
                      v-model.number="remoteOptions.entreBackupTcpPort"
                      type="number"
                      min="0"
                      max="65535"
                      placeholder="2001"
                      :state="getValidationState(validationContext)"
                      :disabled="!entreConnectionFieldsEnabled"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="6">
                <b-form-checkbox
                  v-model="remoteOptions.entreArmDisarmReports"
                  class="mb-1"
                  :disabled="!entreConnectionFieldsEnabled"
                >
                  {{ $t('dmp.remoteOptions.entreArmDisarmReports') }}
                </b-form-checkbox>

                <b-form-checkbox
                  v-model="remoteOptions.entreZoneReports"
                  class="mb-1"
                  :disabled="!entreConnectionFieldsEnabled"
                >
                  {{ $t('dmp.remoteOptions.entreZoneReports') }}
                </b-form-checkbox>

                <b-form-checkbox
                  v-model="remoteOptions.entreUserCommandReports"
                  class="mb-1"
                  :disabled="!entreConnectionFieldsEnabled"
                >
                  {{ $t('dmp.remoteOptions.entreUserCommandReports') }}
                </b-form-checkbox>

                <b-form-checkbox
                  v-model="remoteOptions.entreDoorReports"
                  class="mb-1"
                  :disabled="!entreConnectionFieldsEnabled"
                >
                  {{ $t('dmp.remoteOptions.entreDoorReports') }}
                </b-form-checkbox>

                <b-form-checkbox
                  v-model="remoteOptions.entreSupervisoryReports"
                  class="mb-1"
                  :disabled="!entreConnectionFieldsEnabled"
                >
                  {{ $t('dmp.remoteOptions.entreSupervisoryReports') }}
                </b-form-checkbox>

                <validation-provider
                  #default="validationContext"
                  :name="$t('dmp.remoteOptions.entreCheckInMinutes')"
                  :rules="{ required: true, min_value: 0, max_value: 999 }"
                >
                  <b-form-group
                    :label="$t('dmp.remoteOptions.entreCheckInMinutes')"
                    label-for="entreCheckInMinutes"
                    class="mt-2"
                  >
                    <b-form-input
                      id="entreCheckInMinutes"
                      v-model.number="remoteOptions.entreCheckInMinutes"
                      type="number"
                      min="0"
                      max="999"
                      placeholder="0"
                      :state="getValidationState(validationContext)"
                      :disabled="!entreConnectionFieldsEnabled"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <validation-provider
                  #default="validationContext"
                  :name="$t('dmp.remoteOptions.entrePassphrase')"
                  :rules="{ max: 32 }"
                >
                  <b-form-group :label="$t('dmp.remoteOptions.entrePassphrase')" label-for="entrePassphrase">
                    <b-form-input
                      id="entrePassphrase"
                      v-model="remoteOptions.entrePassphrase"
                      type="password"
                      placeholder=""
                      maxlength="32"
                      :state="getValidationState(validationContext)"
                      :disabled="!entreConnectionFieldsEnabled"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>
    </b-form>
  </validation-observer>
</template>
<script>
import { ref } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import formValidation from "@core/comp-functions/forms/form-validation";
import IpAddressInput from "@/components/InputIp/IpAddressInput.vue";

export default {
  name: "EntreTab",
  components: {
    ValidationObserver,
    ValidationProvider,
    IpAddressInput,
  },
  props: {
    remoteOptions: {
      type: Object,
      required: true,
    },
    entreConnectionOptions: {
      type: Array,
      required: true,
    },
    yesNoOptions: {
      type: Array,
      required: true,
    },
    sendLocalChangesOptions: {
      type: Array,
      required: true,
    },
    entreConnectionFieldsEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    sendLocalChangesIsNone: {
      type: Boolean,
      required: false,
      default: false,
    },
    sendLocalChangesIsNetwork: {
      type: Boolean,
      required: false,
      default: false,
    },
    sendLocalChangesIsDD: {
      type: Boolean,
      required: false,
      default: false,
    },
    remoteChangeIpEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    remoteChangePortEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    remotePhoneNumberEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const { getValidationState } = formValidation();
    const formObserver = ref(null);

    const onSave = async () => {
      const isValid = await formObserver.value.validate();
      if (!isValid) {
        return;
      }
      emit("save");
    };

    return {
      t,
      formObserver,
      getValidationState,
      onSave,
    };
  },
};
</script>

<style scoped lang="scss">
.border {
  border: 1px solid #ebe9f1;
}
</style>
